import React, { useContext, useEffect, useRef } from 'react';
import { BackHandler, Platform } from 'react-native';
import {
  NavigationRouteContext,
  useNavigation,
  usePreventRemove,
} from '@react-navigation/native';

// What a back swipe or the back button dispatches. Anything else (navigate,
// replace, reset) is the app moving on by itself, and is let through.
const BACK_ACTIONS = new Set(['GO_BACK', 'POP', 'POP_TO_TOP']);

interface SheetBackGuardProps {
  /** True while the sheet is up and on top of any other sheet. */
  open: boolean;
  /** False swallows back without closing the sheet, as for swipe-down. */
  dismissible: boolean;
  dismiss: () => void;
}

/**
 * Makes back close an open sheet instead of leaving the screen: the first back
 * press or swipe closes the sheet, the next one navigates.
 *
 * Render it beside the BottomSheetModal, not inside it: the sheet's content is
 * portalled above the navigator, where the screen's navigation is out of reach.
 */
const SheetBackGuard = ({
  open,
  dismissible,
  dismiss,
}: SheetBackGuardProps) => {
  const route = useContext(NavigationRouteContext);
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  // Subscribing on open rather than on mount puts this ahead of the screen's
  // own back handler (Home's exit prompt, Checkout's), as the newest
  // subscription runs first.
  useEffect(() => {
    if (!open || Platform.OS !== 'android') return;
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        if (dismissible) dismissRef.current();
        return true;
      },
    );
    return () => subscription.remove();
  }, [open, dismissible]);

  // The iOS back swipe never reaches BackHandler; the only way to stop it is to
  // block the screen's removal.
  return Platform.OS === 'ios' && route ? (
    <BlockRemoveWhileOpen
      open={open}
      dismissible={dismissible}
      dismissRef={dismissRef}
    />
  ) : null;
};

const BlockRemoveWhileOpen = ({
  open,
  dismissible,
  dismissRef,
}: {
  open: boolean;
  dismissible: boolean;
  dismissRef: React.RefObject<() => void>;
}) => {
  const navigation = useNavigation();
  usePreventRemove(open, ({ data }) => {
    if (!BACK_ACTIONS.has(data.action.type)) {
      navigation.dispatch(data.action);
      return;
    }
    if (dismissible) dismissRef.current();
  });
  return null;
};

export default SheetBackGuard;
